import React from "react";
import { useForm } from "react-hook-form";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../api/axios";
import { useAuth } from "../contexts/AuthContext";
import { mockApiService, shouldUseMockData } from "../mockData/mockLocketData";
import BrandLogo from "../components/shared/BrandLogo";
import LanguageSwitcher from "../components/shared/LanguageSwitcher";
import { useLanguage } from "../contexts/LanguageContext";
import { Home, Eye, EyeOff } from "lucide-react";

const OwnerLoginPage = () => {
  const { locketId } = useParams();
  const navigate = useNavigate();
  const { token, login, logout } = useAuth();
  const { t } = useLanguage();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm();
  const [error, setError] = React.useState(null);
  const [showPassword, setShowPassword] = React.useState(false);

  React.useEffect(() => {
    const storedExpiry = localStorage.getItem("locket_jwt_expiry");
    if (storedExpiry) {
      const expiryTime = parseInt(storedExpiry, 10);
      if (Date.now() >= expiryTime) {
        localStorage.removeItem("locket_jwt");
        localStorage.removeItem("locket_jwt_expiry");
        logout();
      }
    }
  }, [logout]);

  const onSubmit = async (data) => {
    setError(null);
    try {
      let res;
      if (shouldUseMockData()) {
        res = await mockApiService.customerLogin(locketId, data.password);
      } else {
        res = await api.post(`/qr/${locketId}/login`, { password: data.password });
      }
      if (!res.data?.token) {
        setError("Login succeeded but no token received. Please try again.");
        return;
      }
      login(res.data.token);
      const targetLocketId = res.data.customer?.locketId || locketId;
      navigate(`/${targetLocketId}/info`);
    } catch (err) {
      if (!err.response) {
        setError("Unable to reach the server. Please check your internet connection or try again later.");
      } else if (err.response.status === 429) {
        setError("Too many login attempts. Please wait a few minutes and try again.");
      } else if (err.response.status >= 500) {
        setError("Server error. Please try again later.");
      } else {
        const errorMessage = err.response?.data?.message || err.response?.data?.error || "Login failed. Please check your details.";
        setError(errorMessage);
      }
    }
  };

  const handleLogout = () => {
    logout();
    navigate(`/${locketId}/public`);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation bar */}
      <div className="sticky top-0 z-40 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-lg mx-auto px-3 py-2 flex items-center justify-between">
          <BrandLogo logoClassName="h-7" />
          <button
            onClick={() => navigate(`/${locketId}`)}
            className="p-2 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors duration-150"
            title="Back to QR page"
          >
            <Home size={16} className="text-slate-700" />
          </button>
        </div>
      </div>

      <div className="flex flex-col items-center justify-center p-4 pt-8">
        <div className="w-full max-w-sm">
          {/* QR Code ID badge */}
          <div className="text-center mb-4">
            <div className="inline-block bg-slate-100 px-4 py-1.5 rounded-lg border border-slate-200">
              <span className="text-xs font-bold text-slate-500">QR CODE ID:</span>{" "}
              <span className="text-sm font-bold text-red-600">{locketId}</span>
            </div>
          </div>

          <h2 className="text-xl font-extrabold text-slate-900 mb-5 text-center">
            {t("login.title")}
          </h2>

          {token ? (
            <div className="text-center">
              <div className="bg-green-50 border border-green-300 rounded-lg p-6 mb-4">
                <p className="text-green-700 text-base font-semibold">
                  {t("login.alreadyLoggedIn")}
                </p>
              </div>
              <button
                onClick={handleLogout}
                className="w-full py-2.5 px-4 border border-slate-300 rounded-lg bg-white text-slate-700 text-base font-semibold cursor-pointer hover:bg-slate-50 transition-colors duration-150"
              >
                {t("nav.logout")}
              </button>
            </div>
          ) : (
            <div>
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                    {t("login.password")}
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      {...register("password", { required: true })}
                      className={`w-full px-3 py-2.5 pr-12 border rounded-lg text-base focus:outline-none focus:ring-2 transition-colors duration-150 ${
                        errors.password
                          ? "border-red-400 focus:ring-red-200 focus:border-red-400"
                          : "border-slate-300 focus:ring-red-200 focus:border-red-400"
                      }`}
                      placeholder="Enter your password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 bg-transparent border-none cursor-pointer p-1 text-slate-500 hover:text-slate-700 transition-colors"
                      title={showPassword ? "Hide password" : "Show password"}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  {errors.password && (
                    <span className="block text-red-500 text-xs mt-1 font-medium">
                      {t("login.passwordRequired")}
                    </span>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-3 px-4 bg-red-600 hover:bg-red-700 text-white text-base font-bold rounded-lg shadow-md transition-colors duration-150 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed mb-4"
                >
                  {isSubmitting ? t("login.loggingIn") : t("login.title")}
                </button>
              </form>
              <div className="text-center">
                <Link
                  to={`/${locketId}/forgot-password`}
                  className="text-red-600 text-sm font-semibold no-underline hover:underline p-2 inline-block"
                >
                  {t("login.forgotPassword")} &rarr;
                </Link>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-300 rounded-lg p-3 mt-4">
                  <p className="text-red-600 m-0 text-sm font-semibold">{error}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Language switcher */}
      <div className="fixed bottom-4 right-4 z-50 flex flex-col items-center gap-1">
        <LanguageSwitcher />
      </div>
    </div>
  );
};

export default OwnerLoginPage;
